<i18n>
{
  "en": {
    "menuEditorError": "Error loading needed data for menu edition. Please, try it later.",
    "sectionEditorError": "Error loading needed data for section edition. Please, try it later.",
    "changePointOfSaleConfirmation": "Do you want to leave? All unsaved changes will be lost"
  },
  "es": {
    "menuEditorError": "Error cargando para la edición del menú. Inténtelo de nuevo más tarde.",
    "sectionEditorError": "Error cargando para la edición de secciones. Inténtelo de nuevo más tarde.",
    "changePointOfSaleConfirmation": "¿Realmente quieres salir? Perderás todos los cambios no guardados"
  }
}
</i18n>

<template>
  <fragment>
    <dynameat-breadcrumbs :items="currentBreadcrumbs" @menu="onMenuBack"
                          @restaurant="onRestaurantBack"
                          @section="onSectionBack"
                          @clause="onClauseBack"/>
    <section-product-list/>
  </fragment>
</template>

<script>

import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import DynameatBreadcrumbs from '@dynameat/components/DynameatBreadcrumbs';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { Watch } from 'vue-property-decorator';
import { Section } from './models';
import SectionProductList from './components/SectionProductList.vue';

@Component
@Components({ SectionProductList, DynameatBreadcrumbs })
@MapGetters(['currentMenu', 'currentSection', 'selectedPointOfSale', 'isLoading',
  'isClause', 'isSection', 'isSubsection', 'parentClause', 'parentSection', 'getSectionByGuid'
])
export default class MenuEditor extends Vue {
  get currentBreadcrumbs() {
    const breadcrumbs = [{
      text: this.selectedPointOfSale.name,
      disabled: false,
      emitName: 'restaurant'
    }];
    breadcrumbs.push({
      text: this.currentMenu ? this.currentMenu.title : 'Menu',
      disabled: !this.currentSection,
      emitName: 'menu'
    });
    if (this.parentClause) {
      breadcrumbs.push({
        text: this.parentClause.title,
        disabled: false,
        emitName: 'clause'
      });
    }
    if (this.parentSection) {
      breadcrumbs.push({
        text: this.parentSection.title,
        disabled: false,
        emitName: 'section'
      });
    }
    if (this.currentSection && this.currentSection.guid !== Section.CLAUSE_LIST_GUID) {
      breadcrumbs.push({
        text: this.currentSection.title,
        disabled: true,
        emitName: 'null'
      });
    }
    return breadcrumbs;
  }

  @Watch('$route.params.menuGuid')
  onRouteMenuGuidChanged() {
    this.getMenuData();
  }

  @Watch('$route.params.sectionGuid')
  onRouteSectionGuidChanged() {
    this.getSectionData();
  }

  @Watch('$route')
  onRouteChanged(to, from) {
    if (to.params.pointOfSaleGuid !== from.params.pointOfSaleGuid) this.$router.push({ name: 'menuList' });
  }

  async created() {
    await this.getMenuData();
    await this.getSectionData();
  }

  async beforeRouteUpdate(to, from, next) {
    const { params: { pointOfSaleGuid, menuGuid, sectionGuid } } = to;
    const { params: { pointOfSaleGuid: oldPos, menuGuid: oldMenu, sectionGuid: oldSection } } = from;

    if (pointOfSaleGuid !== oldPos || menuGuid !== oldMenu) {
      // eslint-disable-next-line no-alert
      const answer = window.confirm(this.$t('changePointOfSaleConfirmation'));
      if (answer && pointOfSaleGuid !== oldPos) {
        await this.$router.push({ name: 'menuList', params: { pointOfSaleGuid } });
      } else if (answer && menuGuid !== oldMenu) {
        await this.resetMenuEditor();
        await this.$router.push({ name: 'menuEditor', params: { menuGuid } });
      }
    }

    if (sectionGuid !== oldSection) {
      next();
    }
  }

  @MapActions([
    'menuRequest', 'resetMenuEditor', 'loadSection', 'addNotification', 'setLoading',
  ])
  async getMenuData() {
    try {
      await this.setLoading(1);
      await this.resetMenuEditor();
      await this.menuRequest(this.$route.params.menuGuid);
      await this.setLoading(-1);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('menuEditorError'), null));
    }
  }

  async getSectionData() {
    try {
      await this.setLoading(1);
      await this.loadSection({ menuGuid: this.$route.params.menuGuid, sectionGuid: this.$route.params.sectionGuid });
      await this.setLoading(-1);
    } catch (e) {
      if (this.isLoading) {
        await this.setLoading(-1);
      }
      this.addNotification(createErrorNotification(this.$t('sectionEditorError'), null));
    }
  }

  onClauseBack() {
    this.$router.push({
      name: 'sectionEditor',
      params: { menuGuid: this.$route.params.menuGuid, sectionGuid: this.parentClause.guid }
    });
  }

  onSectionBack() {
    this.$router.push({
      name: 'sectionEditor',
      params: { menuGuid: this.$route.params.menuGuid, sectionGuid: this.parentSection.guid }
    });
  }

  onMenuBack() {
    if (this.$route.params.sectionGuid) {
      this.$router.push({ name: 'menuEditor', params: { menuGuid: this.$route.params.menuGuid } });
    }
  }

  onRestaurantBack() {
    const { pointOfSaleGuid } = this.$route.params;
    this.$router.push({ name: 'menuList', params: { pointOfSaleGuid } });
  }
}
</script>
